import { useFetchV2 } from "hooks/fetch/useFetchV2";
import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * Provides a callback function which can be used to execute a mutation an existing object in the api
 * @param method the request type - should be POST, PUT or DELETE
 * @returns {{fetchCallback: ((function(*=, *=): Promise<*>)), loading: boolean, error: boolean}}
 * loading - true when post is currently happening.
 *
 * error - true when an error has occurred.
 *
 * fetchCallback - the callback function, takes a body object and returns a promise which will contain json from the response.
 */
export function useLazyFetchV2(method) {
  const [{ loading, error }, setFetchState] = useState({
    loading: false,
    error: false
  });

  const abortController = useMemo(() => {
    return new AbortController();
  }, []);

  // abort the request when the component using the hook is unmounted
  useEffect(() => {
    return () => abortController.abort();
  }, [abortController]);

  const { callbackFunction } = useFetchV2();

  const fetchCallback = useCallback(
    (url, body, headers = { "Content-Type": "application/json" }) => {
      setFetchState({ loading: true, error: false });
      const jsonBody = JSON.stringify(body);
      return callbackFunction(url, {
        method: method,
        body: jsonBody,
        signal: abortController.signal,
        headers
      }).then(
        response => {
          setFetchState({ loading: false, error: false });
          return Promise.resolve(response);
        },
        error => {
          setFetchState({ loading: false, error: true });
          return Promise.reject(error);
        }
      );
    },
    [abortController.signal, callbackFunction, method]
  );

  return { loading, error, fetchCallback };
}
